import { HonchInstance } from '../config';
import { Logger } from '../utils/logger';

/**
 * Extended interface for internal use — Honch core exposes _onNavigate
 * so we can coordinate with session recording.
 */
interface HonchInternalInstance extends HonchInstance {
    _onNavigate?: (url: string) => void;
}

export class PageviewTracker {
    private honch: HonchInternalInstance;
    private logger: Logger;
    private lastUrl: string = '';
    private originalPushState: typeof history.pushState | null = null;
    private originalReplaceState: typeof history.replaceState | null = null;

    constructor(honch: HonchInstance, logger: Logger) {
        this.honch = honch;
        this.logger = logger;
    }

    start(): void {
        if (typeof window === 'undefined') return;

        this.lastUrl = window.location.href;

        this.patchHistoryMethod('pushState');
        this.patchHistoryMethod('replaceState');

        window.addEventListener('popstate', this.handleUrlChange);

        this.logger.debug('Pageview tracker started');
    }

    stop(): void {
        if (typeof window === 'undefined') return;

        window.removeEventListener('popstate', this.handleUrlChange);

        // FIX: Restore original history methods on stop.
        if (this.originalPushState) {
            history.pushState = this.originalPushState;
            this.originalPushState = null;
        }
        if (this.originalReplaceState) {
            history.replaceState = this.originalReplaceState;
            this.originalReplaceState = null;
        }

        this.logger.debug('Pageview tracker stopped');
    }

    private handleUrlChange = (): void => {
        const currentUrl = window.location.href;

        if (currentUrl !== this.lastUrl) {
            this.lastUrl = currentUrl;
            this.trackPageview();
            if (this.honch._onNavigate) {
                this.honch._onNavigate(currentUrl);
            }
        }
    };

    private trackPageview(): void {
        this.honch.capture('$pageview', {
            $current_url: window.location.href,
            $pathname: window.location.pathname,
            $search: window.location.search,
            $hash: window.location.hash,
            $title: document.title,
            $referrer: document.referrer,
        });

        this.logger.debug('Pageview tracked', { url: window.location.href });
    }

    private patchHistoryMethod(method: 'pushState' | 'replaceState'): void {
        const original = history[method];

        // Store original for restoration
        if (method === 'pushState') {
            this.originalPushState = original;
        } else {
            this.originalReplaceState = original;
        }

        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;

        history[method] = function (...args: Parameters<typeof history.pushState>) {
            const result = original.apply(this, args);
            self.handleUrlChange();
            return result;
        };
    }
}
